/* ============================================================================
   Mock scenario control (development tool).
   Makes loading / empty / error / slow states reproducible instead of hoping
   they appear — every data surface can be reviewed in all four states.
   ========================================================================== */

export type MockScenario = 'normal' | 'empty' | 'error' | 'slow'

const STORAGE_KEY = 'econinhursag.mockScenario'

export interface ScenarioOption {
  value: MockScenario
  label: string
  description: string
}

export const SCENARIO_OPTIONS: readonly ScenarioOption[] = [
  {
    value: 'normal',
    label: 'Normal',
    description: 'Realistic responses from the demo data set.',
  },
  {
    value: 'empty',
    label: 'Empty',
    description: 'Collections come back empty — reviews every empty state.',
  },
  {
    value: 'error',
    label: 'Error',
    description: 'Read requests fail with a 503 — reviews every recoverable error state.',
  },
  {
    value: 'slow',
    label: 'Slow (x4)',
    description: 'Simulated latency is tripled — reviews skeletons and disabled actions.',
  },
]

let current: MockScenario = readStoredScenario()
const listeners = new Set<() => void>()
let version = 0

function readStoredScenario(): MockScenario {
  try {
    const stored = globalThis.localStorage.getItem(STORAGE_KEY)
    if (stored === 'empty' || stored === 'error' || stored === 'slow') return stored
  } catch {
    /* storage blocked — fall through to the default */
  }
  return 'normal'
}

export function getMockScenario(): MockScenario {
  return current
}

export function setMockScenario(next: MockScenario): void {
  if (next === current) return
  current = next
  version += 1
  try {
    if (next === 'normal') globalThis.localStorage.removeItem(STORAGE_KEY)
    else globalThis.localStorage.setItem(STORAGE_KEY, next)
  } catch {
    /* non-fatal: the scenario still applies for this session */
  }
  for (const listener of listeners) listener()
}

export function subscribeMockScenario(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Bumped on every scenario change so data hooks can re-run. */
export function getMockScenarioVersion(): number {
  return version
}

export function describeScenario(scenario: MockScenario): ScenarioOption {
  return SCENARIO_OPTIONS.find((option) => option.value === scenario) ?? SCENARIO_OPTIONS[0]
}
